import { createContext } from 'react'

import type { Listing, MessageAttachment } from '@app/models'
import { Semaphore } from '@app/utils/async'

import { getListingIdsFromText } from './listingLinkUtils'
import type {
  AttachmentListingSemaphore,
  AttachmentListingsContextValue,
  ConversationMessage,
  MessageKeySource,
} from './types'

export const listingSemaphores = new Map<number, Semaphore>()

export const sharedFetchedListings = new Map<number, Listing>()

export const missingAttachmentListingIds = new Set<number>()

export const fetchingAttachmentListingIds = new Set<number>()

export const normalizeUrl = (url: string) => url.replace(/\/+$/, '')

export const isListingAttachment = (
  attachment: MessageAttachment
): attachment is Extract<MessageAttachment, { kind: 'Listing' }> =>
  attachment.kind === 'Listing'

export const isFileAttachment = (
  attachment: MessageAttachment
): attachment is Extract<MessageAttachment, { kind: 'File' }> =>
  attachment.kind === 'File'

export const isImageMimeType = (mimeType?: string) => {
  if (!mimeType) {
    return false
  }

  return mimeType.toLowerCase().startsWith('image/')
}

export const getListingSemaphore = (
  listingId: number
): AttachmentListingSemaphore => {
  let semaphore = listingSemaphores.get(listingId)
  if (!semaphore) {
    semaphore = new Semaphore(1)
    listingSemaphores.set(listingId, semaphore)
  }
  return semaphore
}

export const AttachmentListingsContext =
  createContext<AttachmentListingsContextValue | null>(null)

export const getListingAttachmentIds = (
  attachments?: MessageAttachment[]
): number[] => {
  if (!attachments?.length) {
    return []
  }

  return attachments
    .filter(isListingAttachment)
    .map((attachment) => attachment.listingId)
}

export const getMessageKey = (message: MessageKeySource): string => {
  if (message.id) {
    return `id:${message.id}`
  }

  if (message.uuid) {
    return `uuid:${message.uuid}`
  }

  if (message.acsChatMessageId) {
    return `acs:${message.acsChatMessageId}`
  }

  return `created:${message.creationDate}`
}

export const getMessageListingData = (
  message: ConversationMessage
): number[] => {
  const listingIdsFromAttachments = getListingAttachmentIds(message.attachments)
  const listingIdsFromText = getListingIdsFromText(message.content?.text)
  const uniqueIds = new Set<number>()

  listingIdsFromAttachments.forEach((id) => uniqueIds.add(id))
  listingIdsFromText.forEach((id) => uniqueIds.add(id))

  return [...uniqueIds].sort((a, b) => a - b)
}
